import { getSecurityStatus, vaultOpen } from './api'

/**
 * When to try a dropped agent socket again, and how long to wait.
 *
 * Extracted because there are TWO sockets onto an agent and only one of them used to
 * come back. The terminal view has always reconnected; the headless socket a
 * transcript view holds for the owner's typing did not — it reported the close and
 * stopped. So after any drop, Clean View kept looking fine and could no longer send
 * anything, until the component happened to remount. The owner reported exactly that:
 * sometimes text cannot be sent, and the terminal behind it is closed.
 *
 * The POLICY lives here and the loops stay separate. They are not the same code: the
 * terminal writes "[disconnected — reconnecting…]" into itself, which the headless one
 * cannot do because it has no terminal. Sharing the presentation would mean inventing
 * a place to put a message that has nowhere to go; sharing the timing is what actually
 * has to agree, or the two views recover at different speeds from the same outage and
 * only one of them looks broken.
 */

/** First wait after a drop. */
export const RETRY_BASE_MS = 1000
/** Ceiling, so a long outage settles into a poll rather than a hammer. */
export const RETRY_MAX_MS = 10_000
/** Locked vault: `/ws` would only 401, so wait longer and check again. */
export const RETRY_LOCKED_MS = 3000

export function nextDelay(previous: number): number {
  return Math.min(previous * 2, RETRY_MAX_MS)
}

/**
 * How long before the next attempt, given the vault's state.
 *
 * Asks the server rather than assuming: a locked vault refuses `/ws` outright, so
 * retrying on the normal backoff is a request that cannot succeed, repeated faster and
 * faster. If the status call itself fails the server is probably restarting — which IS
 * worth retrying — so that falls through to the ordinary backoff rather than being
 * treated as a lock.
 */
export async function delayBeforeRetry(previous: number): Promise<number> {
  try {
    const st = await getSecurityStatus()
    /*
     * vaultOpen, NOT `st.unlocked` — the same bug TerminalView fixed inline and this
     * shared copy kept (its comment there calls `unlocked` "the fourth copy of this
     * exact bug today"; this module was the fifth). On a VAULTLESS deployment — which
     * is this whole fleet — `unlocked` is false forever, so every retry answered
     * RETRY_LOCKED_MS and the headless socket poked every 3s indefinitely instead of
     * backing off to 10s. Not a freeze (it did reconnect), but a permanent 3s poll
     * during any outage, on battery, from a screen nobody is looking at.
     *
     * `vaultOpen` treats configured:false as open, which is what "no vault exists"
     * means; a vault that EXISTS and is locked still waits here, which is the case
     * this branch was written for.
     */
    if (!vaultOpen(st)) return RETRY_LOCKED_MS
  } catch {
    // Unreachable: restarting, most likely. Fall through and back off normally.
  }
  return nextDelay(previous)
}

/**
 * Should a retry be scheduled at all right now?
 *
 * No, while the page is hidden — and on a phone that is most of the time. Android's
 * WebView keeps timers running when backgrounded: `Bridge.java` defaults `KeepRunning`
 * to true, this project does not override it, and `pauseTimers()` sits inside the
 * branch that only runs when it is false. avengents-app-builder read the installed
 * source rather than recalling it. So without this, a dropped socket has a screen
 * nobody is looking at polling `/security/status` every three to ten seconds, on the
 * user's battery. Locking the screen counts; so does switching apps.
 *
 * iOS is deliberately NOT assumed from that. Whether the OS suspends the whole app is
 * the OS's decision, not something Capacitor's code answers, and they marked it as
 * needing a device rather than inferring it. This guard is right either way, which is
 * why it does not need the answer.
 *
 * `visibilitychange` rather than a Capacitor lifecycle plugin: seventeen call sites in
 * this app already use it, so it is proven IN THIS WEBVIEW rather than merely
 * standard — and a plugin would cost a physical reinstall on every device, which is a
 * real price paid in their layer for precision this does not need.
 */
export function shouldScheduleRetry(): boolean {
  return typeof document === 'undefined' || !document.hidden
}

/*
 * A better signal exists and is NOT being used yet, deliberately.
 *
 * Both native shells already dispatch OS lifecycle events to the document without any
 * plugin — iOS at `CapacitorBridge.swift:266-275` (scene did-enter-background /
 * will-enter-foreground → "pause" / "resume"), Android at
 * `MockCordovaWebViewImpl.java:130` (handlePause → "pause"). That is the OS saying so,
 * where `document.hidden` is the web layer inferring it: Capacitor never touches the
 * Android WebView's visibility, so `hidden` there depends on Chromium mapping the
 * Android view hierarchy onto it, and whether that mapping exists is not answerable
 * from source.
 *
 * Not switched because nobody has watched those events fire — app-builder read the
 * code and marked both as conditional (the iOS branch needs `injectCordovaFiles ==
 * false`; the Android one needs a non-null cordovaWebView). Trading a known-safe
 * predicate for an unobserved one is the wrong direction, and this one fails safe:
 * if `hidden` never goes true, the cost is extra heartbeats, not an unreachable agent.
 *
 * When they confirm it on a device, this function is the only place to change — the
 * two reconnect loops and the busy heartbeat all ask it, so it is one edit rather than
 * three that could drift.
 */
